import { GetContactsRequestDTO, GetUsersRequestDTO, MAX_USERS_QUERY_LIMIT, UserStatus } from '@gearbelt/global';
import { infiniteQueryOptions, keepPreviousData, queryOptions } from '@tanstack/react-query';

import { ApiService } from '@/services/api-v2';

export const userKeys = {
  all: ['users'] as const,
  stats: () => [...userKeys.all, 'stats'] as const,
  infinite: (search: string, orderBy?: GetUsersRequestDTO['orderBy']) => [...userKeys.all, 'infinite', search, orderBy] as const,
  contactsInfinite: (filters?: GetContactsRequestDTO['filters'], orderBy?: GetContactsRequestDTO['orderBy']) =>
    [...userKeys.all, 'contacts-infinite', filters, orderBy] as const,
  activeContributors: () => [...userKeys.all, 'active-contributors'] as const,
};

export const getActiveContributorsQueryOptions = () =>
  queryOptions({
    queryKey: userKeys.activeContributors(),
    retry: false,
    queryFn: async () => {
      const { data } = await (await ApiService.GetUsers({ limit: MAX_USERS_QUERY_LIMIT, filters: { userType: 'contributors' } })).json();
      // Active = invite accepted (emailCode cleared server-side). Pending/inactive
      // contributors can't open a shared link, so they're excluded from the picker.
      return data.filter((contributor) => contributor.status === UserStatus.Active);
    },
  });

export const sitemateUserKeys = {
  all: ['sitemate-users'] as const,
  stats: () => [...sitemateUserKeys.all, 'stats'] as const,
  infinite: (search: string, orderBy?: GetUsersRequestDTO['orderBy']) => [...sitemateUserKeys.all, 'infinite', search, orderBy] as const,
};

export const getUsersStatsQueryOptions = () => {
  return queryOptions({
    queryKey: userKeys.stats(),
    queryFn: async () => (await ApiService.GetUsersStats({})).json(),
  });
};

export const getUsersInfiniteQueryOptions = ({
  search,
  orderBy,
  enabled = true,
}: {
  search: string;
  orderBy?: GetUsersRequestDTO['orderBy'];
  enabled?: boolean;
}) => {
  return infiniteQueryOptions({
    queryKey: userKeys.infinite(search, orderBy),
    queryFn: async ({ pageParam }: { pageParam?: string }) =>
      await (await ApiService.GetUsers({ limit: 30, cursors: pageParam, filters: { search }, orderBy })).json(),
    getNextPageParam: (lastPage) => (lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined),
    initialPageParam: undefined as string | undefined,
    retry: false,
    gcTime: 0,
    placeholderData: keepPreviousData,
    enabled,
  });
};

export const getSitemateUsersInfiniteQueryOptions = ({
  search,
  orderBy,
  enabled = true,
}: {
  search: string;
  orderBy?: GetUsersRequestDTO['orderBy'];
  enabled?: boolean;
}) => {
  return infiniteQueryOptions({
    queryKey: sitemateUserKeys.infinite(search, orderBy),
    queryFn: async ({ pageParam }: { pageParam?: string }) =>
      await (await ApiService.GetUsers({ limit: 30, cursors: pageParam, filters: { search, userType: 'sitemate-users' }, orderBy })).json(),
    getNextPageParam: (lastPage) => (lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined),
    initialPageParam: undefined as string | undefined,
    retry: false,
    refetchOnMount: false,
    gcTime: 0,
    placeholderData: keepPreviousData,
    enabled,
  });
};

export const getContactsInfiniteQueryOptions = ({
  filters,
  orderBy,
  enabled = true,
}: {
  filters?: GetContactsRequestDTO['filters'];
  orderBy?: GetContactsRequestDTO['orderBy'];
  enabled?: boolean;
}) => {
  return infiniteQueryOptions({
    queryKey: userKeys.contactsInfinite(filters, orderBy),
    queryFn: async ({ pageParam }: { pageParam?: string }) => await (await ApiService.GetContacts({ limit: 30, cursors: pageParam, filters, orderBy })).json(),
    getNextPageParam: (lastPage) => (lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined),
    initialPageParam: undefined as string | undefined,
    retry: false,
    gcTime: 0,
    placeholderData: keepPreviousData,
    enabled,
  });
};

export const getSitemateUsersStatsQueryOptions = () => {
  return queryOptions({
    queryKey: sitemateUserKeys.stats(),
    queryFn: async () => (await ApiService.GetUsersStats({ sitemateUsers: { total: true, active: true } })).json(),
  });
};
